import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
const _excluded = ["styles", "makeStyles", "isGroup"];
var _dec, _class, _FormFieldGroup;
/*
 * The MIT License (MIT)
 *
 * Copyright (c) 2015 - present Instructure, Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */

import { Component, Children } from 'react';
import { Grid } from '@instructure/ui-grid';
import { pickProps, omitProps } from '@instructure/ui-react-utils';
import { withStyle } from '@instructure/emotion';
import { FormFieldLayout } from "../FormFieldLayout/index.js";
import generateStyle from "./styles.js";
import generateComponentTheme from "./theme.js";
import { allowedProps } from "./props.js";
import { jsx as _jsx } from "@emotion/react/jsx-runtime";
/**
---
category: components
---
**/
let FormFieldGroup = (_dec = withStyle(generateStyle, generateComponentTheme), _dec(_class = (_FormFieldGroup = class FormFieldGroup extends Component {
  constructor(...args) {
    super(...args);
    this.ref = null;
    this.handleRef = el => {
      const elementRef = this.props.elementRef;
      this.ref = el;
      if (typeof elementRef === 'function') {
        elementRef(el);
      }
    };
  }
  componentDidMount() {
    var _this$props$makeStyle, _this$props;
    (_this$props$makeStyle = (_this$props = this.props).makeStyles) === null || _this$props$makeStyle === void 0 ? void 0 : _this$props$makeStyle.call(_this$props, this.makeStylesVariables);
  }
  componentDidUpdate() {
    var _this$props$makeStyle2, _this$props2;
    (_this$props$makeStyle2 = (_this$props2 = this.props).makeStyles) === null || _this$props$makeStyle2 === void 0 ? void 0 : _this$props$makeStyle2.call(_this$props2, this.makeStylesVariables);
  }
  get makeStylesVariables() {
    // new form errors dont need borders
    const oldInvalid = !!this.props.messages && this.props.messages.findIndex(message => {
      return message.type === 'error';
    }) >= 0;
    return {
      invalid: oldInvalid
    };
  }
  get invalid() {
    return !!this.props.messages && this.props.messages.findIndex(message => {
      return message.type === 'error' || message.type === 'newError';
    }) >= 0;
  }
  renderColumns() {
    return Children.map(this.props.children, (child, index) => {
      return child ? _jsx(Grid.Col, {
        width: child.props && child.props.width ? 'auto' : void 0,
        children: child
      }, index) : null;
    });
  }
  renderChildren() {
    return _jsx(Grid, {
      colSpacing: this.props.colSpacing,
      rowSpacing: this.props.rowSpacing,
      vAlign: this.props.vAlign,
      startAt: this.props.startAt || (this.props.layout === 'columns' ? 'medium' : null),
      children: _jsx(Grid.Row, {
        children: this.renderColumns()
      })
    });
  }
  renderFields() {
    const styles = this.props.styles;
    return _jsx("span", {
      css: styles === null || styles === void 0 ? void 0 : styles.formFieldGroup,
      children: this.renderChildren()
    }, "fields");
  }
  render() {
    const _this$props3 = this.props,
      styles = _this$props3.styles,
      makeStyles = _this$props3.makeStyles,
      isGroup = _this$props3.isGroup,
      props = _objectWithoutProperties(_this$props3, _excluded);
    // This is quite ugly, but according to ARIA spec the `aria-invalid` prop
    // can only be used with certain roles see
    // https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Attributes/aria-invalid#associated_roles
    // `aria-invalid` is put on in FormFieldLayout because the error message
    // DOM part gets there its ID.
    let ariaInvalid = void 0;
    if (this.props.role && this.invalid && ['application', 'checkbox', 'combobox', 'gridcell', 'listbox', 'radiogroup', 'slider', 'spinbutton', 'textbox', 'tree', 'columnheader', 'rowheader', 'searchbox', 'switch', 'treegrid'].includes(this.props.role)) {
      ariaInvalid = 'true';
    }
    return _jsx(FormFieldLayout, {
      ...omitProps(props, FormFieldGroup.allowedProps),
      ...pickProps(props, FormFieldLayout.allowedProps),
      vAlign: props.vAlign,
      layout: props.layout === 'inline' ? 'inline' : 'stacked',
      label: props.description,
      "aria-disabled": props.disabled ? 'true' : void 0,
      "aria-invalid": ariaInvalid,
      elementRef: this.handleRef,
      isGroup: isGroup,
      children: this.renderFields()
    });
  }
}, _FormFieldGroup.displayName = "FormFieldGroup", _FormFieldGroup.componentId = 'FormFieldGroup', _FormFieldGroup.allowedProps = allowedProps, _FormFieldGroup.defaultProps = {
  as: 'fieldset',
  disabled: false,
  rowSpacing: 'medium',
  colSpacing: 'small',
  vAlign: 'middle',
  isGroup: true
}, _FormFieldGroup)) || _class);
export default FormFieldGroup;
export { FormFieldGroup };